// frontend/src/components/Common/RichTextEditor.jsx
// Generic rich text editor — supports formatting, image paste/drop.
// Renamed from RichTextEmailEditor; API-compatible (same props).

import React, { useEffect, useRef, useCallback, useState } from 'react';
import './RichTextEditor.css';
import { attachmentsAPI } from '../../api/client';

// ── Toolbar button ────────────────────────────────────────────────────────────
const Btn = ({ title, active, onClick, children }) => (
  <button
    type="button"
    title={title}
    className={`rte-btn${active ? ' rte-btn--active' : ''}`}
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
);

const Sep = () => <span className="rte-sep"/>;

// ── Main component ────────────────────────────────────────────────────────────
/**
 * Props:
 *   value          – HTML string (controlled)
 *   onChange(html) – called whenever content changes
 *   onPasteImage   – fn(file) → { tempId, previewUrl }  (for pending upload pattern)
 *   placeholder    – shown when empty
 *   minHeight      – px (default 220)
 *   readOnly       – boolean
 */
const RichTextEditor = ({
  value = '',
  onChange,
  onPasteImage,
  placeholder = '',
  minHeight = 220,
  readOnly = false,
  pendingFiles = [],
  onPendingFilesChange,
  onToast,
  // Saved-file attachment props (shows DB files below editor)
  attachEntityType,
  attachEntityId,
  attachCategory = '',
  // fn(message, onConfirm) — caller shows a toast-confirm; falls back to window.confirm
  onConfirmDelete,
}) => {
  const editorRef   = useRef(null);
  const fileInputRef = useRef(null);
  const [savedFiles, setSavedFiles] = React.useState([]);
  const skipSync    = useRef(false);   // prevent cursor-jump on re-sync
  const [activeFormats, setActiveFormats] = useState({});

  const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB

  const handleAttachFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (onPendingFilesChange) onPendingFilesChange([...pendingFiles, ...files]);
    e.target.value = '';
  };
  const removeAttachment = (i) => {
    if (onPendingFilesChange) onPendingFilesChange(pendingFiles.filter((_, j) => j !== i));
  };
  const formatFileSize = (b) =>
    b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

  const deleteSavedFile = (file) => {
    const doDelete = async () => {
      try {
        await attachmentsAPI.delete(file.attachment_id);
        setSavedFiles(prev => prev.filter(f => f.attachment_id !== file.attachment_id));
      } catch { onToast?.('Erro ao eliminar ficheiro', 'error'); }
    };
    if (onConfirmDelete) {
      onConfirmDelete(`Eliminar ficheiro "${file.original_filename}"?`, doDelete);
    } else {
      if (window.confirm(`Eliminar "${file.original_filename}"?`)) doDelete();
    }
  };

  const supersedeFile = async (file) => {
    if (!window.confirm(`Marcar "${file.original_filename}" como substituído? O ficheiro ficará visível mas inativo.`)) return;
    try {
      await attachmentsAPI.supersede(file.attachment_id);
      // Reload attachments to reflect new status
      if (attachEntityType && attachEntityId) {
        attachmentsAPI.getAttachments(attachEntityType, attachEntityId, attachCategory)
          .then(r => { if (r.data.success) setSavedFiles(r.data.attachments || []); });
      }
    } catch (e) {
      console.error('Supersede error:', e);
    }
  };

  const downloadFile = (file) => {
    // attachmentsAPI.download() returns a URL string (token-based)
    const url = attachmentsAPI.download(file.attachment_id);
    window.open(url, '_blank');
  };

  // Sync external value → DOM only when it genuinely differs
  useEffect(() => {
    if (!editorRef.current) return;
    if (skipSync.current) { skipSync.current = false; return; }
    const current = editorRef.current.innerHTML;
    if (current !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Load saved attachments for this category when entityId is available
  useEffect(() => {
    if (!attachEntityType || !attachEntityId) { setSavedFiles([]); return; }
    attachmentsAPI.getAttachments(attachEntityType, attachEntityId, attachCategory)
      .then(r => { if (r.data.success) setSavedFiles(r.data.attachments || []); })
      .catch(() => {});
  }, [attachEntityType, attachEntityId, attachCategory]);

  const emit = useCallback(() => {
    skipSync.current = true;
    onChange?.(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const exec = useCallback((cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    emit();
    updateFormats();
  }, [emit]);

  const updateFormats = () => {
    // Only report active formats when the cursor/selection is inside the editor
    const sel = window.getSelection();
    const editorEl = editorRef.current;
    if (!sel || !editorEl) { setActiveFormats({}); return; }
    const anchor = sel.anchorNode;
    const insideEditor = anchor && editorEl.contains(anchor);
    if (!insideEditor) { setActiveFormats({}); return; }
    setActiveFormats({
      bold:          document.queryCommandState('bold'),
      italic:        document.queryCommandState('italic'),
      underline:     document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertOrderedList:   document.queryCommandState('insertOrderedList'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
    });
  };

  // ── Image injection helper ────────────────────────────────────────────────
  const insertImageBase64 = useCallback((file) => {
    if (file.size > MAX_IMAGE_BYTES) {
      onToast?.(`Imagem demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). M\u00e1ximo: 2 MB.`, 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const html = `<img src="${e.target.result}" alt="" class="rte-inline-img"/>`;
      document.execCommand('insertHTML', false, html);
      emit();
    };
    reader.readAsDataURL(file);
  }, [emit, onToast, MAX_IMAGE_BYTES]);

  // Legacy path: parent supplies onPasteImage (pending-upload pattern)
  const insertImagePending = useCallback((file) => {
    if (!onPasteImage) return;
    const pending = onPasteImage(file);
    if (!pending) return;
    const html = `<img src="${pending.previewUrl}" data-pending-image-id="${pending.tempId}" alt="" class="rte-inline-img"/>`;
    document.execCommand('insertHTML', false, html);
    emit();
  }, [onPasteImage, emit]);

  // ── Paste handler ─────────────────────────────────────────────────────────
  const handlePaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const images = items.filter(i => i.type.startsWith('image/'));

    if (images.length > 0) {
      e.preventDefault();
      editorRef.current?.focus();
      images.forEach(item => {
        const file = item.getAsFile();
        if (!file) return;
        // Prefer base64 inline (auth-free) unless caller explicitly handles uploads
        if (onPasteImage) {
          insertImagePending(file);
        } else {
          insertImageBase64(file);
        }
      });
      return;
    }

    // Strip external HTML formatting — paste as plain text to avoid style leakage
    // unless user explicitly wants formatting (Ctrl+Shift+V keeps original)
    const html = e.clipboardData?.getData('text/html');
    if (html) {
      e.preventDefault();
      // Keep basic semantic tags, strip class/style/id
      const cleaned = sanitizePaste(html);
      document.execCommand('insertHTML', false, cleaned);
      emit();
      return;
    }

    setTimeout(emit, 0);
  }, [onPasteImage, insertImagePending, insertImageBase64, emit]);

  // ── Drop handler ──────────────────────────────────────────────────────────
  const handleDrop = useCallback((e) => {
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    e.preventDefault();
    editorRef.current?.focus();
    files.forEach(file => {
      if (onPasteImage) {
        insertImagePending(file);
      } else {
        insertImageBase64(file);
      }
    });
  }, [onPasteImage, insertImagePending, insertImageBase64]);

  // ── Link insertion ────────────────────────────────────────────────────────
  const insertLink = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString() || '';
    const url = window.prompt('URL do link:', 'https://');
    if (!url) return;
    if (selectedText) {
      exec('createLink', url);
    } else {
      const text = window.prompt('Texto do link:', url) || url;
      document.execCommand('insertHTML', false, `<a href="${url}" target="_blank">${text}</a>`);
      editorRef.current?.focus();
      emit();
    }
  }, [exec, emit]);

  // ── Heading ───────────────────────────────────────────────────────────────

  return (
    <div className={`rte-wrapper${readOnly ? ' rte-wrapper--readonly' : ''}`}>

      {/* ── Toolbar ── */}
      {!readOnly && (
        <div className="rte-toolbar" onMouseDown={e => e.preventDefault()}>

          {/* Text style */}
          <Btn title="Negrito (Ctrl+B)" active={activeFormats.bold}
            onClick={() => exec('bold')}><b>B</b></Btn>
          <Btn title="Itálico (Ctrl+I)" active={activeFormats.italic}
            onClick={() => exec('italic')}><i>I</i></Btn>
          <Btn title="Sublinhado (Ctrl+U)" active={activeFormats.underline}
            onClick={() => exec('underline')}><u>U</u></Btn>
          <Btn title="Riscado" active={activeFormats.strikeThrough}
            onClick={() => exec('strikeThrough')}><s>S</s></Btn>

          <Sep/>

          {/* Lists */}
          <Btn title="Lista com marcas" active={activeFormats.insertUnorderedList}
            onClick={() => exec('insertUnorderedList')}>• —</Btn>
          <Btn title="Lista numerada" active={activeFormats.insertOrderedList}
            onClick={() => exec('insertOrderedList')}>1.</Btn>

          <Sep/>

          {/* Alignment */}
          <Btn title="Alinhar à esquerda" onClick={() => exec('justifyLeft')}>⫷</Btn>
          <Btn title="Centrar" onClick={() => exec('justifyCenter')}>≡</Btn>
          <Btn title="Alinhar à direita" onClick={() => exec('justifyRight')}>⫸</Btn>

          <Sep/>

          {/* Indent */}
          <Btn title="Aumentar recuo" onClick={() => exec('indent')}>→|</Btn>
          <Btn title="Diminuir recuo" onClick={() => exec('outdent')}>|←</Btn>

          <Sep/>

          {/* Link */}
          <Btn title="Inserir link" onClick={insertLink}>🔗</Btn>

          {/* Horizontal rule */}
          <Btn title="Linha horizontal" onClick={() => {
            exec('insertHorizontalRule');
          }}>─</Btn>

          <Sep/>

          {/* Colors — label wraps input so clicking anywhere triggers the picker */}
          <label className="rte-color-wrap" title="Cor do texto" onMouseDown={e => e.preventDefault()}>
            <span className="rte-color-label">A</span>
            <input type="color" className="rte-color-input"
              defaultValue="#000000"
              onInput={e => exec('foreColor', e.target.value)}/>
          </label>

          <label className="rte-color-wrap" title="Realce" onMouseDown={e => e.preventDefault()}>
            <span className="rte-color-label rte-highlight-label">H</span>
            <input type="color" className="rte-color-input"
              defaultValue="#ffff00"
              onInput={e => exec('hiliteColor', e.target.value)}/>
          </label>

          <Sep/>

          {/* Clear */}
          <Btn title="Limpar formatação" onClick={() => exec('removeFormat')}>✕</Btn>

          {onPendingFilesChange && (
            <>
              <Sep/>
              <label className="rte-btn rte-attach-btn" title="Anexar ficheiro(s)">
                <span className="rte-attach-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19
                      a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                  <span>Anexar</span>
                </span>
                <input ref={fileInputRef} type="file" multiple
                  style={{ display: 'none' }} onChange={handleAttachFiles} />
              </label>
            </>
          )}
        </div>
      )}

      {/* ── Pending image indicator ── */}
      {/* Rendered by parent — this component just provides the hook */}

      {/* ── Editable area ── */}
      <div
        ref={editorRef}
        className="rte-content"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onKeyUp={updateFormats}
        onMouseUp={updateFormats}
        onBlur={() => setActiveFormats({})}
        onPaste={handlePaste}
        onDrop={handleDrop}
      />

      {savedFiles.length > 0 && (
        <div className="rte-attachments">
          {savedFiles.map((file) => (
            <div key={file.attachment_id}
              className={`rte-attachment-item${file.is_superseded ? ' rte-attachment-item--superseded' : ''}`}>
              <span className="rte-attachment-icon">
                {file.is_superseded ? '🔄' : '📄'}
              </span>
              <button type="button" className="rte-attachment-name rte-attachment-link"
                onClick={() => downloadFile(file)} title={file.original_filename}
                style={file.is_superseded ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}}>
                {file.original_filename}
              </button>
              {file.is_superseded && (
                <span className="rte-attachment-superseded-badge">substituído</span>
              )}
              <span className="rte-attachment-uploader">
                {file.uploaded_by_name && <span>{file.uploaded_by_name}</span>}
                {file.uploaded_at && (
                  <span className="rte-attachment-date">
                    {' · '}{new Date(file.uploaded_at).toLocaleDateString('pt-PT', {day:'2-digit',month:'short',year:'2-digit'})}
                  </span>
                )}
              </span>
              {!readOnly && !file.is_superseded && (
                <button type="button" className="rte-attachment-supersede"
                  onClick={() => supersedeFile(file)} title="Marcar como substituído">🔄</button>
              )}
              {!readOnly && (
                <button type="button" className="rte-attachment-remove"
                  onClick={() => deleteSavedFile(file)} title="Eliminar">×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && pendingFiles.length > 0 && (
        <div className="rte-attachments">
          {pendingFiles.map((file, i) => (
            <div key={i} className="rte-attachment-item">
              <span className="rte-attachment-icon">📄</span>
              <span className="rte-attachment-name" title={file.name}>{file.name}</span>
              <span className="rte-attachment-pending">pendente</span>
              <button type="button" className="rte-attachment-remove"
                onClick={() => removeAttachment(i)} title="Remover">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Paste sanitiser: keep semantic tags, strip styles/classes ─────────────────
function sanitizePaste(html) {
  const div = document.createElement('div');
  div.innerHTML = html;

  // ── Pre-pass: remove elements and node types that should never appear ────────
  // Covers <style>, <script>, <head>, <meta>, <link>, HTML comments, etc.
  // Outlook emails wrap CSS in <!-- --> inside a <style> tag — both must go.
  const STRIP_TAGS = new Set(['style','script','head','meta','link','title']);

  const strip = (node) => {
    // Remove comment nodes (HTML comments carry Outlook CSS verbatim)
    if (node.nodeType === Node.COMMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (STRIP_TAGS.has(node.tagName.toLowerCase())) {
        node.parentNode?.removeChild(node);
        return;
      }
    }
    // Recurse on a snapshot (childNodes is live)
    Array.from(node.childNodes).forEach(strip);
  };
  Array.from(div.childNodes).forEach(strip);

  const KEEP_TAGS = new Set([
    'p','br','b','strong','i','em','u','s','strike',
    'h1','h2','h3','h4','ul','ol','li','a','img',
    'table','thead','tbody','tr','th','td',
    'blockquote','code','pre','hr',
  ]);

  const clean = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.cloneNode();
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const tag = node.tagName.toLowerCase();
    if (!KEEP_TAGS.has(tag)) {
      // Replace unknown block elements with their children
      const frag = document.createDocumentFragment();
      node.childNodes.forEach(ch => {
        const cleaned = clean(ch);
        if (cleaned) frag.appendChild(cleaned);
      });
      return frag;
    }

    const el = document.createElement(tag);
    // Only keep href/src attributes
    if (tag === 'a' && node.getAttribute('href'))
      el.setAttribute('href', node.getAttribute('href'));
    if (tag === 'a') el.setAttribute('target', '_blank');
    if (tag === 'img' && node.getAttribute('src'))
      el.setAttribute('src', node.getAttribute('src'));

    node.childNodes.forEach(ch => {
      const cleaned = clean(ch);
      if (cleaned) el.appendChild(cleaned);
    });
    return el;
  };

  const out = document.createElement('div');
  div.childNodes.forEach(ch => {
    const cleaned = clean(ch);
    if (cleaned) out.appendChild(cleaned);
  });
  return out.innerHTML || div.textContent || '';
}

export default RichTextEditor;